import { apply, give, toast } from '../core/effects';
import { bus } from '../core/bus';
import { S } from '../core/game';
import { sfx } from '../audio/sfx';
import { ARRIVALS, type MapId } from '../world/map';
import { px } from '../world/mapdef';
import type { World } from '../world/world';
import { endDay } from '../content/dayCycle';
import { forceWeather, type WeatherKind } from '../content/weather';
import { h } from './dom';
import { closePanel, isPanelOpen, openPanel, refreshPanel } from './panel';

/** Cheat panel for testing: only with `?debug` in the URL. Texts in Spanish on purpose (tester tool). */
const PLACES: [string, MapId, number, number][] = [
  ['🏠 Casa', 'home', px(12), px(15) + 6],
  ['🏫 Colegio', 'school', px(19), px(28) - 2],
  ['🌳 Parque', 'town', px(42), px(38)],
  ['🚉 Estación', 'station', px(10), px(8) + 8],
  ['🏮 Mercado', 'market', px(4), px(11)],
  ['🏙️ Taipéi', 'taipei', ARRIVALS.taipei!.x, ARRIVALS.taipei!.y],
  ['🏛️ Museo', 'museum', ARRIVALS.museum!.x, ARRIVALS.museum!.y],
  ['🛍️ Ximending', 'ximen', ARRIVALS.ximen!.x, ARRIVALS.ximen!.y],
  ['🏮 Longshan', 'longshan', ARRIVALS.longshan!.x, ARRIVALS.longshan!.y],
  ['🏯 CKS', 'cks', ARRIVALS.cks!.x, ARRIVALS.cks!.y],
  ['♨️ Beitou', 'beitou', ARRIVALS.beitou!.x, ARRIVALS.beitou!.y],
  ['🚉 Hualien ciudad', 'hlcity', ARRIVALS.hlcity!.x, ARRIVALS.hlcity!.y],
  ['⛰️ Taroko', 'hualien', ARRIVALS.hualien!.x, ARRIVALS.hualien!.y],
  ['🌱 Balcón', 'balcony', px(3), px(5)],
  ['✈️ Aeropuerto', 'airport', ARRIVALS.airport!.x, ARRIVALS.airport!.y],
  ['🚄 Kaohsiung', 'kaohsiung', ARRIVALS.kaohsiung!.x, ARRIVALS.kaohsiung!.y],
  ['🚄 Tainan', 'tainan', ARRIVALS.tainan!.x, ARRIVALS.tainan!.y],
  ['🇪🇸 Barcelona', 'barcelona', ARRIVALS.barcelona!.x, ARRIVALS.barcelona!.y],
];
const WEATHER: [string, WeatherKind][] = [['☀️', 'sunny'], ['☁️', 'cloudy'], ['🌧️', 'rain'], ['⛈️', 'storm'], ['🧥', 'cool'], ['🌀', 'typhoon']];

export function initCheats(world: World): void {
  const btn = h('button', { class: 'cheat-btn', 'aria-label': 'cheats', onclick: () => open(world) }, '🛠️');
  document.getElementById('ui')?.append(btn);
}

function open(world: World): void {
  openPanel('cheats', () => {
    const s = S();
    const b = (label: string, fn: () => void) =>
      h('button', { class: 'chip', onclick: () => (fn(), sfx('pop'), bus.emit('change'), isPanelOpen('cheats') && refreshPanel('cheats')) }, label);
    const go = (map: MapId, x: number, y: number) => {
      closePanel('cheats');
      world.teleport(map, x, y, 'up');
    };
    return {
      title: '🛠️ Trucos',
      className: 'cheats',
      body: [
        h('h4', null, `🪙 ${s.coins} · ⭐ ${s.stars}`),
        h(
          'div',
          { class: 'chips' },
          b('+100 🪙', () => apply({ coins: 100 })),
          b('+1000 🪙', () => apply({ coins: 1000 })),
          b('+50 ⭐', () => apply({ stars: 50 })),
          b('⚡❤️ al máximo', () => apply({ energy: 100, mood: 100 })),
          b('🚽 vaciar', () => (s.stats.bladder = 0)),
          b('🍓×3 🍅 🌻 ☂️', () => (give('strawberries', 3), give('tomato'), give('sunflower'), give('umbrella'))),
        ),
        h('h4', null, `🕒 ${String(Math.floor(s.time / 60)).padStart(2, '0')}:${String(Math.floor(s.time % 60)).padStart(2, '0')} · semana ${s.week}`),
        h(
          'div',
          { class: 'chips' },
          b('8:00', () => (s.time = 8 * 60)),
          b('12:00', () => (s.time = 12 * 60)),
          b('17:30', () => (s.time = 17 * 60 + 30)),
          b('20:00', () => (s.time = 20 * 60)),
          b('+1 h', () => (s.time += 60)),
          b('🌙 Día siguiente', () => (closePanel('cheats'), endDay())),
          b('📅 Semana +1 (festival)', () => (s.week += 1, toast(`Semana ${s.week}`))),
        ),
        h('h4', null, '🌦️ Tiempo de hoy'),
        h('div', { class: 'chips' }, WEATHER.map(([icon, kind]) => b(icon, () => forceWeather(s.dayIndex, kind, true)))),
        h('h4', null, '🗺️ Ir a'),
        h('div', { class: 'chips' }, PLACES.map(([label, map, x, y]) => b(label, () => go(map, x, y)))),
        h('h4', null, '🎫 Viajes'),
        h(
          'div',
          { class: 'chips' },
          b('Billete Taipéi', () => (s.daily.ticketTaipei = true)),
          b('Billete Hualien', () => (s.daily.ticketHualien = true)),
          b('Billete Kaohsiung', () => (s.daily.ticketKaohsiung = true)),
          b('Billete Tainan', () => (s.daily.ticketTainan = true)),
          b('✈️ Tarjeta de embarque + seguridad', () => ((s.daily.flightBarcelona = true), (s.daily.security = true))),
          b('⛑️ Casco', () => (s.daily.helmet = true)),
        ),
      ],
    };
  });
}
